/* assets/fr-currency.js
 *
 * Currency switcher for Fritschi House.
 *
 * The shop settles in EUR (no Shopify Payments, so no native multi-currency).
 * This replaces the displayed price with the chosen currency and says plainly,
 * in the picker and under the cart total, that the charge is in EUR.
 *
 * -- How the prices are found ----------------------------------------------
 * Settings > General > Currency formatting wraps every Liquid price in
 *
 *   <span class="money" data-eur="{{amount}}">EUR{{amount_with_comma_separator}}</span>
 *
 * so the exact euro amount arrives as a machine-readable attribute. Nothing is
 * parsed out of localised text unless data-eur is missing (older cached HTML,
 * app output), in which case the text is read as a fallback.
 *
 * -- Why it does not drift --------------------------------------------------
 * Every element keeps its euro base and its original markup. A redraw always
 * starts from the base, so converting twice cannot compound, and switching
 * back to EUR restores the exact Liquid output rather than a reformat of it.
 *
 * Horizon repaints prices by swapping DOM (variant picker, cart drawer, quick
 * add, section rendering), so a MutationObserver on childList catches them.
 * characterData is watched too, for the rarer case of a script rewriting the
 * text inside an existing span; our own writes are fenced off with a flag.
 *
 * -- Rates ------------------------------------------------------------------
 * Shopify publishes the rates it uses itself at /services/javascripts/
 * currencies.js. Same origin, no third party, no key. Cached for six hours in
 * localStorage. If the request fails the baked ECB table below takes over, so
 * the page never ends up half converted.
 *
 * Debug from the console: FRCurrency.debug()
 */
(function () {
  'use strict';

  var CFG = window.FR_CURRENCY_CONFIG || {};
  var BASE = (CFG.base || 'EUR').toUpperCase();
  var LIST = (CFG.list || []).map(function (c) { return String(c).toUpperCase(); });
  var NOTE = CFG.note || '';
  var GEO = (CFG.country || '').toUpperCase();
  var STORE_KEY = 'fr_currency';
  var RATE_KEY = 'fr_rates_v1';
  var RATE_TTL = 6 * 60 * 60 * 1000;

  if (LIST.indexOf(BASE) === -1) LIST.unshift(BASE);
  if (LIST.length < 2) return;

  /* code -> [symbol, symbol goes after the number, rounding step] */
  var META = {
    EUR: ['€', false, 1], GBP: ['£', false, 1], USD: ['$', false, 1],
    CHF: ['CHF ', false, 1], CAD: ['CA$', false, 1], AUD: ['A$', false, 1],
    NZD: ['NZ$', false, 1], SEK: [' kr', true, 10], NOK: [' kr', true, 10],
    DKK: [' kr', true, 5], PLN: [' zł', true, 5], CZK: [' Kč', true, 10],
    JPY: ['¥', false, 100], HKD: ['HK$', false, 1], SGD: ['S$', false, 1],
    AED: ['AED ', false, 1], ILS: ['₪', false, 1]
  };

  /* 1 EUR = n. ECB, 2026-09-14. Only used if Shopify's rate file is unreachable. */
  var FALLBACK = {
    EUR: 1, USD: 1.1551, GBP: 0.85598, CHF: 0.9431, CAD: 1.6041, AUD: 1.6202,
    NZD: 2.0012, SEK: 11.281, NOK: 10.767, DKK: 7.4753, PLN: 4.3418,
    CZK: 24.294, JPY: 178.52, HKD: 9.0599, SGD: 1.4676, AED: 4.2421, ILS: 3.527
  };

  var GEO_CURRENCY = {
    GB: 'GBP', US: 'USD', CH: 'CHF', CA: 'CAD', AU: 'AUD', NZ: 'NZD',
    SE: 'SEK', NO: 'NOK', DK: 'DKK', PL: 'PLN', CZ: 'CZK', JP: 'JPY',
    HK: 'HKD', SG: 'SGD', AE: 'AED', IL: 'ILS'
  };

  var SKIP = '[data-fr-noconv]';

  var originals = new WeakMap();   /* element -> untouched Liquid markup */
  var bases = new WeakMap();       /* element -> amount in BASE, as a number */
  var justWrote = new Set();       /* elements this pass repainted, so the observer can tell
                                      our own work apart from the theme's without going deaf
                                      to everything else happening in the same tick */
  var current = BASE;
  var rates = null;
  var ratesSettled = false;   /* the rate table is final for this page load */
  var revision = 0;           /* bumped when the rate table changes, so a price
                                 painted with an older table is repainted and two
                                 different figures for one product cannot coexist */

  /* -- storage helpers, all failure tolerant -------------------------------- */
  function read(key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } }
  function write(key, val) { try { window.localStorage.setItem(key, val); } catch (e) {} }

  /* -- rates ---------------------------------------------------------------- */
  function cachedRates() {
    var raw = read(RATE_KEY);
    if (!raw) return null;
    try {
      var box = JSON.parse(raw);
      if (!box || !box.at || Date.now() - box.at > RATE_TTL) return null;
      return box.rates && box.rates[BASE] ? box.rates : null;
    } catch (e) { return null; }
  }

  function fetchRates() {
    return fetch('/services/javascripts/currencies.js', { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(r.status); })
      .then(function (text) {
        var m = text.match(/rates\s*:\s*(\{[^}]*\})/);
        if (!m) throw new Error('no rates in file');
        var usd = JSON.parse(m[1]);
        if (!usd[BASE]) throw new Error('base currency missing');
        /* Shopify quotes USD per unit. Re-base onto BASE = 1. */
        var out = {};
        for (var code in usd) {
          if (Object.prototype.hasOwnProperty.call(usd, code) && usd[code] > 0) {
            out[code] = usd[BASE] / usd[code];
          }
        }
        write(RATE_KEY, JSON.stringify({ at: Date.now(), rates: out }));
        return out;
      });
  }

  /* -- number handling ------------------------------------------------------ */
  function toNumber(s) {
    if (s === null || s === undefined) return NaN;
    var t = String(s).replace(/[^\d.,-]/g, '');
    if (!t) return NaN;
    var tail = t.search(/[.,]\d{1,2}$/);
    if (tail > -1) {
      /* A separator one or two digits from the end is the decimal mark. */
      var mark = t.charAt(tail);
      var other = mark === ',' ? '.' : ',';
      t = t.split(other).join('');
      t = t.slice(0, t.lastIndexOf(mark)) + '.' + t.slice(t.lastIndexOf(mark) + 1);
    } else {
      t = t.replace(/[.,]/g, '');
    }
    var n = parseFloat(t);
    return isFinite(n) ? n : NaN;
  }

  function baseOf(el) {
    if (bases.has(el)) return bases.get(el);
    /* Normally data-eur is the truth. But if something rewrote the text inside
       an existing span, that fresh text is the new price and data-eur is stale,
       so the text wins for exactly that one read. */
    var dirty = el.hasAttribute('data-fr-dirty');
    var n = dirty ? toNumber(el.textContent) : toNumber(el.getAttribute('data-eur'));
    if (!isFinite(n)) n = dirty ? toNumber(el.getAttribute('data-eur')) : toNumber(el.textContent);
    el.removeAttribute('data-fr-dirty');
    if (!isFinite(n)) return NaN;
    bases.set(el, n);
    return n;
  }

  /* Fixed prices from the snippet: "<currency> <base amount> <shown amount>"
     per line. A price point the shop wants to own, instead of whatever the
     rate happens to produce that day. */
  var FIXED = (function () {
    var table = {};
    String(CFG.fixed || '').split('\n').forEach(function (line) {
      var m = line.trim().match(/^([A-Za-z]{3})\s+([\d.]+)\s+([\d.]+)$/);
      if (!m) return;
      var code = m[1].toUpperCase();
      table[code] = table[code] || {};
      table[code][parseFloat(m[2]).toFixed(2)] = parseFloat(m[3]);
    });
    return table;
  })();

  function fixedFor(amount, code) {
    var table = FIXED[code];
    if (!table) return null;

    var exact = table[amount.toFixed(2)];
    if (exact != null) return exact;

    /* Two of a fixed-price item should read as twice that price, not as the
       rate's idea of the doubled euro figure. */
    for (var base in table) {
      if (!Object.prototype.hasOwnProperty.call(table, base)) continue;
      var b = parseFloat(base);
      if (!(b > 0)) continue;
      var n = amount / b;
      var whole = Math.round(n);
      if (whole > 1 && whole <= 20 && Math.abs(n - whole) < 0.0001) return table[base] * whole;
    }
    return null;
  }

  function format(amount, code) {
    var meta = META[code] || [code + ' ', false, 1];
    var sym = meta[0], after = meta[1], step = meta[2];

    var value = fixedFor(amount, code);
    var dec;

    if (value !== null) {
      dec = Math.abs(value % 1) < 0.005 ? 0 : 2;
    } else {
      var rate = (rates && rates[code]) || FALLBACK[code];
      if (!rate) return null;
      value = amount * rate;

      /* Currencies whose smallest sensible unit is bigger than one — yen,
         the kronor, zloty, koruna — are always rounded to that unit and never
         carry decimals. A price of 16,229.44 yen is not a price anyone writes.
         The rest keep cents, except that a round euro figure stays round. */
      var wholeIn = Math.abs(amount % 1) < 0.005;
      if (step > 1) {
        dec = 0;
        value = Math.round(value / step) * step;
      } else if (wholeIn) {
        dec = 0;
        value = Math.round(value / step) * step;
      } else {
        dec = 2;
        value = Math.round(value * 100) / 100;
      }
    }

    var text = value.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });
    return after ? text + sym : sym + text;
  }

  /* -- painting ------------------------------------------------------------- */
  function collect(root) {
    var out = [];
    if (!root || root.nodeType !== 1) return out;
    if (root.matches && root.matches('.money')) out.push(root);
    if (root.querySelectorAll) {
      var found = root.querySelectorAll('.money');
      for (var i = 0; i < found.length; i++) out.push(found[i]);
    }
    return out.filter(function (el) { return !(el.closest && el.closest(SKIP)); });
  }

  function paint(el) {
    if (!originals.has(el)) originals.set(el, el.innerHTML);
    if (el.getAttribute('data-fr-shown') === current + '#' + revision) return;

    if (current === BASE) {
      justWrote.add(el);
      el.innerHTML = originals.get(el);
      el.removeAttribute('data-fr-shown');
      return;
    }
    var amount = baseOf(el);
    if (!isFinite(amount)) return;
    var text = format(amount, current);
    if (text === null) return;
    justWrote.add(el);
    el.textContent = text;
    el.setAttribute('data-fr-shown', current + '#' + revision);
  }

  function run(roots) {
    arm();   /* independent of rates: the picker may appear right away */

    /* Hold the first foreign-currency paint until the rate table is final.
       Painting with the baked fallback and correcting it a moment later would
       leave whichever prices were already on screen at the old figure. */
    if (current !== BASE && !ratesSettled) return;
    try {
      var list = roots && roots.length ? roots : [document.body];
      for (var i = 0; i < list.length; i++) {
        var els = collect(list[i]);
        for (var j = 0; j < els.length; j++) paint(els[j]);
      }
      document.documentElement.setAttribute('data-fr-currency', current);
      arm();
      notice();
    } catch (e) {
      if (window.console) console.warn('[fr-currency]', e);
    }
    /* Observer callbacks are microtasks, so they have all seen our writes by
       the time this frame runs. Safe to forget them now. */
    window.requestAnimationFrame(function () { justWrote.clear(); });
  }

  /* Until the money format is wrapped there is nothing to convert, so the
     picker stays out of the header instead of offering a switch that does
     nothing. CSS keeps it hidden; this attribute is what reveals it. */
  var warned = false;
  function arm() {
    if (document.querySelector('.money')) {
      document.documentElement.setAttribute('data-fr-armed', '');
      return true;
    }
    document.documentElement.removeAttribute('data-fr-armed');
    if (!warned && window.console) {
      warned = true;
      console.warn('[fr-currency] no .money elements on this page. Settings > General > ' +
        'Currency formatting must wrap prices in a span with class "money" carrying ' +
        'data-eur="{{amount}}". Until then every price stays in EUR.');
    }
    return false;
  }

  /* -- the "charged in EUR" line under any cart total ------------------------ */
  function notice() {
    var hosts = document.querySelectorAll('[data-fr-note]');
    for (var i = 0; i < hosts.length; i++) {
      hosts[i].textContent = current === BASE ? '' : NOTE;
      hosts[i].hidden = current === BASE || !NOTE;
    }
  }

  /* -- selection ------------------------------------------------------------ */
  function set(code, remember) {
    code = String(code || '').toUpperCase();
    if (LIST.indexOf(code) === -1) return;
    current = code;
    if (remember !== false) write(STORE_KEY, code);
    run();
    document.dispatchEvent(new CustomEvent('fr:currency', { detail: { currency: code } }));
    syncAll();
  }

  /* The picker upgrades before start() has read the stored choice, so its
     label has to be refreshed once the real currency is known. */
  function syncAll() {
    var pickers = document.querySelectorAll('fr-currency');
    for (var i = 0; i < pickers.length; i++) if (pickers[i].sync) pickers[i].sync();
  }

  /* ?currency=USD forces a currency from any browser, anywhere in the world.
     ?currency=auto forgets the stored choice and detects by country again,
     which is how you look at the shop the way a first-time visitor does. */
  function fromUrl() {
    var m = (window.location.search || '').match(/[?&](?:currency|fr_currency)=([A-Za-z]{3,4})/);
    return m ? m[1].toUpperCase() : null;
  }

  function initial() {
    var forced = fromUrl();
    if (forced === 'AUTO') {
      try { window.localStorage.removeItem(STORE_KEY); } catch (e) {}
    } else if (forced && LIST.indexOf(forced) !== -1) {
      write(STORE_KEY, forced);
      return forced;
    }
    var saved = read(STORE_KEY);
    if (saved && LIST.indexOf(saved.toUpperCase()) !== -1) return saved.toUpperCase();
    var geo = GEO_CURRENCY[GEO];
    return geo && LIST.indexOf(geo) !== -1 ? geo : BASE;
  }

  /* -- the picker ----------------------------------------------------------- */
  class Picker extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;
      var self = this;

      var options = LIST.map(function (code) {
        var sym = (META[code] || [''])[0] || '';
        return '<li role="option" id="frc-' + code + '" data-code="' + code + '" aria-selected="false">' +
               '<span class="frc__code">' + code + '</span>' +
               '<span class="frc__sym">' + sym.trim() + '</span></li>';
      }).join('');

      this.innerHTML =
        '<button class="frc__btn" type="button" aria-haspopup="listbox" aria-expanded="false" aria-label="Currency">' +
          '<span class="frc__now">' + current + '</span>' +
          '<svg class="frc__caret" viewBox="0 0 10 6" aria-hidden="true">' +
            '<path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
        '<div class="frc__panel" hidden>' +
          '<ul class="frc__list" role="listbox" tabindex="-1">' + options + '</ul>' +
          (NOTE ? '<p class="frc__note" data-fr-noconv>' + NOTE + '</p>' : '') +
        '</div>';

      this._btn = this.querySelector('.frc__btn');
      this._panel = this.querySelector('.frc__panel');
      this._list = this.querySelector('.frc__list');

      this._btn.addEventListener('click', function (e) { e.preventDefault(); self.toggle(); });

      this._list.addEventListener('click', function (e) {
        var li = e.target.closest('li[data-code]');
        if (!li) return;
        set(li.getAttribute('data-code'));
        self.close(true);
      });

      this._list.addEventListener('keydown', function (e) {
        var items = Array.prototype.slice.call(self._list.children);
        var at = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var next = items[(at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
          if (next) { next.tabIndex = -1; next.focus(); }
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (at > -1) { set(items[at].getAttribute('data-code')); self.close(true); }
        }
      });

      this.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.close(true); });
      document.addEventListener('click', function (e) { if (!self.contains(e.target)) self.close(); });

      this.sync();
    }

    toggle() { if (this._panel.hidden) this.open(); else this.close(); }

    open() {
      this._panel.hidden = false;
      this._btn.setAttribute('aria-expanded', 'true');
      var sel = this._list.querySelector('[aria-selected="true"]') || this._list.firstElementChild;
      if (sel) { sel.tabIndex = -1; sel.focus(); }
    }

    close(refocus) {
      if (!this._panel || this._panel.hidden) return;
      this._panel.hidden = true;
      this._btn.setAttribute('aria-expanded', 'false');
      if (refocus) this._btn.focus();
    }

    sync() {
      if (!this._built) return;
      this.querySelector('.frc__now').textContent = current;
      var items = this._list.children;
      for (var i = 0; i < items.length; i++) {
        var on = items[i].getAttribute('data-code') === current;
        items[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
      this._list.setAttribute('aria-activedescendant', 'frc-' + current);
    }
  }

  if (!window.customElements.get('fr-currency')) window.customElements.define('fr-currency', Picker);

  /* -- boot ----------------------------------------------------------------- */
  function watch() {
    var queued = [];
    var scheduled = false;

    function flush() {
      scheduled = false;
      var roots = queued.length > 40 ? [document.body] : queued;
      queued = [];
      run(roots);
    }

    new MutationObserver(function (records) {
      var hit = false;
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        var target = rec.target;

        /* Did this touch the inside of a price we already know? Either the
           theme rewrote it (repaint from the new text) or we did (ignore). */
        var host = null;
        if (target) {
          if (target.nodeType === 3 && target.parentElement && target.parentElement.closest) {
            host = target.parentElement.closest('.money');
          } else if (target.nodeType === 1 && target.closest) {
            host = target.closest('.money');
          }
        }
        if (host) {
          if (justWrote.has(host)) continue;
          bases.delete(host);
          originals.delete(host);
          host.removeAttribute('data-fr-shown');
          host.setAttribute('data-fr-dirty', '');
          queued.push(host);
          hit = true;
          continue;
        }

        for (var j = 0; j < rec.addedNodes.length; j++) {
          if (rec.addedNodes[j].nodeType === 1) { queued.push(rec.addedNodes[j]); hit = true; }
        }
      }
      if (hit && !scheduled) { scheduled = true; window.requestAnimationFrame(flush); }
    }).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  }

  function start() {
    current = initial();
    syncAll();
    run();
    watch();
    window.setTimeout(arm, 800);   /* prices that only arrive after hydration */
    document.addEventListener('shopify:section:load', function (e) { run([e.target]); });
    window.addEventListener('pageshow', function () { run(); });

    function settle(table) {
      if (ratesSettled && rates === table) return;
      rates = table || null;          /* null means the baked table takes over */
      ratesSettled = true;
      revision++;
      run();
    }

    var cached = cachedRates();
    if (cached) {
      settle(cached);
      return;
    }
    /* Fresh visit: give Shopify's rate file a moment, then fall back rather
       than leave a foreign-currency visitor looking at euro prices. */
    var waited = false;
    var giveUp = window.setTimeout(function () { waited = true; settle(null); }, 1500);
    fetchRates()
      .then(function (r) { if (!waited) { window.clearTimeout(giveUp); settle(r); } })
      .catch(function () { if (!waited) { window.clearTimeout(giveUp); settle(null); } });
  }

  window.FRCurrency = {
    set: set,
    get: function () { return current; },
    list: function () { return LIST.slice(); },
    debug: function () {
      var els = document.querySelectorAll('.money');
      var tagged = 0;
      for (var i = 0; i < els.length; i++) if (els[i].getAttribute('data-eur')) tagged++;
      return {
        currency: current,
        base: BASE,
        list: LIST,
        country: GEO,
        priceElements: els.length,
        withDataAttribute: tagged,
        ratesSource: rates ? 'shopify' : 'baked fallback',
        hundredBaseUnits: format(100, current)
      };
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
